import React, { useState, useEffect } from 'react';

const TDSCalculator = () => {
  const [amount, setAmount] = useState('');
  const [tdsType, setTdsType] = useState('194C');
  const [panAvailable, setPanAvailable] = useState(true);
  const [customRate, setCustomRate] = useState('');
  
  const [results, setResults] = useState(null);

  const tdsRates = {
    '194C': { name: 'Payment to Contractors', individual: 1, company: 2, noPan: 20 },
    '194J': { name: 'Professional/Technical Services', individual: 10, company: 10, noPan: 20 },
    '194H': { name: 'Commission/Brokerage', individual: 5, company: 5, noPan: 20 },
    '194I': { name: 'Rent - Land/Building', individual: 10, company: 10, noPan: 20 },
    '194IA': { name: 'Sale of Immovable Property', individual: 1, company: 1, noPan: 20 },
    '194IB': { name: 'Rent Payment by Individual', individual: 5, company: 5, noPan: 20 },
    '194D': { name: 'Insurance Commission', individual: 5, company: 5, noPan: 20 },
    '194G': { name: 'Commission on Lottery', individual: 5, company: 5, noPan: 20 },
    '192': { name: 'Salary Income', individual: 0, company: 0, noPan: 0 },
    '194A': { name: 'Interest other than on Securities', individual: 10, company: 10, noPan: 20 },
    'custom': { name: 'Custom TDS Rate', individual: 0, company: 0, noPan: 0 },
  };

  useEffect(() => {
    if (amount && !isNaN(amount) && parseFloat(amount) > 0) {
      calculateTDS();
    } else {
      setResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, tdsType, panAvailable, customRate]);

  const calculateTDS = () => {
    const baseAmount = parseFloat(amount);
    let tdsRate = 0;

    if (tdsType === 'custom') {
      tdsRate = parseFloat(customRate) || 0;
    } else {
      const rates = tdsRates[tdsType];
      tdsRate = panAvailable ? rates.individual : rates.noPan;
    }

    const tdsAmount = (baseAmount * tdsRate) / 100;
    const netPayable = baseAmount - tdsAmount;

    // Calculate quarterly TDS if applicable
    const quarterlyTDS = tdsAmount / 4;

    setResults({
      grossAmount: baseAmount.toFixed(2),
      tdsRate: tdsRate.toFixed(2),
      tdsAmount: tdsAmount.toFixed(2),
      netPayable: netPayable.toFixed(2),
      quarterlyTDS: quarterlyTDS.toFixed(2),
      section: tdsType,
      sectionName: tdsRates[tdsType].name,
      panStatus: panAvailable ? 'Available' : 'Not Available',
    });
  };

  const getTDSDueDates = () => {
    return [
      { quarter: 'Q1 (Apr-Jun)', dueDate: 'July 31' },
      { quarter: 'Q2 (Jul-Sep)', dueDate: 'October 31' },
      { quarter: 'Q3 (Oct-Dec)', dueDate: 'January 31' },
      { quarter: 'Q4 (Jan-Mar)', dueDate: 'May 31' },
    ];
  };

  const copyToClipboard = () => {
    if (!results) return;
    
    const summary = `
📊 TDS Calculation Summary
━━━━━━━━━━━━━━━━━━━━━━━━━━
Section: ${results.section} - ${results.sectionName}
PAN: ${results.panStatus}
━━━━━━━━━━━━━━━━━━━━━━━━━━
Gross Amount: ₹${results.grossAmount}
TDS Rate: ${results.tdsRate}%
TDS Amount: ₹${results.tdsAmount}
━━━━━━━━━━━━━━━━━━━━━━━━━━
Net Payable: ₹${results.netPayable}
━━━━━━━━━━━━━━━━━━━━━━━━━━
Generated via Vyqda.com/tools
    `;
    
    navigator.clipboard.writeText(summary.trim());
    alert('✅ TDS summary copied to clipboard!');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Input Section */}
      <div className="card">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-2">📊</span> TDS Calculator
        </h2>

        <div className="space-y-5">
          {/* Amount */}
          <div>
            <label className="label">Payment Amount</label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter payment amount"
                className="input-field pl-8"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* TDS Section */}
          <div>
            <label className="label">TDS Section</label>
            <select value={tdsType} onChange={(e) => setTdsType(e.target.value)} className="input-field">
              {Object.entries(tdsRates).map(([key, value]) => (
                <option key={key} value={key}>
                  {key === 'custom' ? value.name : `Section ${key} - ${value.name}`}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Rate Input */}
          {tdsType === 'custom' && (
            <div className="animate-slide-up">
              <label className="label">Custom TDS Rate (%)</label>
              <input
                type="number"
                value={customRate}
                onChange={(e) => setCustomRate(e.target.value)}
                placeholder="Enter TDS rate"
                className="input-field"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          )}

          {/* PAN Availability */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <span className="font-semibold text-gray-700 block">PAN Available</span>
              <span className="text-xs text-gray-500">
                {panAvailable ? 'Normal TDS rate applies' : '20% TDS rate applies (No PAN)'}
              </span>
            </div>
            <button
              onClick={() => setPanAvailable(!panAvailable)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                panAvailable ? 'bg-success-600' : 'bg-red-500'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  panAvailable ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* TDS Rate Info */}
          {tdsType !== 'custom' && (
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-900 mb-2">TDS Rate Info</h4>
              <div className="text-sm text-amber-800 space-y-1">
                <div className="flex justify-between">
                  <span>Individual/HUF:</span>
                  <span className="font-semibold">{tdsRates[tdsType].individual}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Company:</span>
                  <span className="font-semibold">{tdsRates[tdsType].company}%</span>
                </div>
                <div className="flex justify-between">
                  <span>No PAN:</span>
                  <span className="font-semibold text-red-700">{tdsRates[tdsType].noPan}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Threshold Limits Info */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-2 flex items-center">
              <span className="mr-2">ℹ️</span> Threshold Limits
            </h4>
            <ul className="text-xs text-indigo-800 space-y-1">
              <li>• 194C (Contractor): ₹30,000 (single) / ₹1,00,000 (annual)</li>
              <li>• 194J (Professional): ₹30,000 per year</li>
              <li>• 194H (Commission): ₹15,000 per year</li>
              <li>• 194I (Rent): ₹2,40,000 per year</li>
              <li>• 194IB (Rent by Individual): ₹50,000 per month</li>
              <li>• 194IA (Property Sale): ₹50,00,000</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="space-y-6">
        {results ? (
          <>
            <div className="card">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📊</span> TDS Breakdown
              </h3>

              <div className="space-y-4">
                {/* Section Info */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="font-semibold text-blue-900 mb-1">
                    Section {results.section}
                  </div>
                  <div className="text-sm text-blue-700">{results.sectionName}</div>
                  <div className="mt-2 text-xs text-blue-600">
                    PAN Status: <span className="font-semibold">{results.panStatus}</span>
                  </div>
                </div>

                {/* Amount Breakdown */}
                <div className="result-card">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Gross Payment</span>
                      <span className="text-2xl font-bold text-gray-900">₹{results.grossAmount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">TDS Rate</span>
                      <span className="text-xl font-semibold text-primary-600">{results.tdsRate}%</span>
                    </div>
                    <div className="flex justify-between items-center border-t pt-3">
                      <span className="text-gray-600">TDS Amount</span>
                      <span className="text-2xl font-bold text-red-600">-₹{results.tdsAmount}</span>
                    </div>
                  </div>
                </div>

                {/* Net Payable */}
                <div className="result-card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-semibold">Net Payable to Vendor</span>
                    <span className="text-3xl font-bold text-success-600">₹{results.netPayable}</span>
                  </div>
                </div>

                {/* Quarterly TDS */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2">Quarterly Payment</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-purple-700">TDS per Quarter</span>
                    <span className="text-xl font-bold text-purple-600">₹{results.quarterlyTDS}</span>
                  </div>
                </div>

                {/* TDS Due Dates */}
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-4 border border-orange-200">
                  <h4 className="font-semibold text-orange-900 mb-3 flex items-center">
                    <span className="mr-2">📅</span> TDS Payment Due Dates
                  </h4>
                  <div className="space-y-2">
                    {getTDSDueDates().map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-orange-700 font-medium">{item.quarter}</span>
                        <span className="text-orange-900 font-semibold">{item.dueDate}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="card">
              <h4 className="font-semibold text-gray-700 mb-3">Quick Actions</h4>
              <div className="flex flex-wrap gap-3">
                <button onClick={copyToClipboard} className="btn-primary flex items-center">
                  <span className="mr-2">📋</span> Copy Summary
                </button>
                <button onClick={() => window.print()} className="btn-secondary flex items-center no-print">
                  <span className="mr-2">🖨️</span> Print
                </button>
              </div>
            </div>

            {/* Important Notes */}
            <div className="card bg-gradient-to-br from-red-50 to-pink-50 border border-red-200">
              <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                <span className="mr-2">⚠️</span> Important Notes
              </h4>
              <ul className="text-sm text-red-800 space-y-1">
                <li>• TDS must be deposited by 7th of next month</li>
                <li>• File quarterly TDS return (24Q/26Q/27Q)</li>
                <li>• Issue Form 16A/16 to deductee</li>
                <li>• Late payment attracts 1.5% interest per month</li>
                <li>• Late filing penalty: ₹200 per day</li>
                <li>• Always verify TAN and challan details</li>
              </ul>
            </div>

            {/* Pro Tips */}
            <div className="card bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200">
              <h4 className="font-semibold text-cyan-900 mb-2 flex items-center">
                <span className="mr-2">💡</span> Pro Tips
              </h4>
              <ul className="text-sm text-cyan-800 space-y-1">
                <li>• Use TRACES portal for TDS filing</li>
                <li>• Generate Form 26AS regularly</li>
                <li>• Reconcile TDS with books quarterly</li>
                <li>• Issue Form 16A within 15 days of filing</li>
                <li>• Lower TDS certificate (197) can reduce rate</li>
                <li>• NIL TDS certificate for startups available</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Ready to Calculate TDS</h3>
            <p className="text-gray-500">Enter payment amount to see TDS breakdown</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TDSCalculator;

